<template>
  <div>
    <!-- PWA 安装按钮 -->
    <div v-if="canInstall && props.isImage" class="pwa-install-btn" @click="handleInstall">
      <!-- <v-fab icon="mdi-tray-arrow-down" color="#fff"></v-fab> -->
      <img src="@/assets/images/h5/download.png" alt="TT Share" />
    </div>
    <v-overlay v-model="showInstallPrompt" :scrim="'rgba(0,0,0,0.55)'" class="pwa-overlay">
      <div class="pwa-bar" @click.stop>
        <div class="pwa-content">
          <img class="pwa-icon" src="@/assets/images/78tt/header-logo-icon.png" alt="78TT" />
          <div class="pwa-message">
            Gostaríamos de adicionar o tesouro.com à sua tela inicial para as últimas apostas grátis e atualizações de bônus.
          </div>
        </div>
        <div class="pwa-actions">
          <div class="pwa-action cancel" @click="closeAutoPrompt">Cancelar</div>
          <div class="pwa-action install" @click="confirmAutoPrompt">Instalar</div>
        </div>
      </div>
    </v-overlay>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from "vue";
import { registerSW } from "virtual:pwa-register";
import { showWarning } from "@/utils/toast";
import { getIsApk } from "@/utils/appEnv";
const props = defineProps(["isImage"]);
const canInstall = ref(false);
const showInstallPrompt = ref(false);
let deferredPrompt: any = null;
const autoPromptClosed = ref(false);

const isIOS = () => {
  return (
    [
      "iPad Simulator",
      "iPhone Simulator",
      "iPod Simulator",
      "iPad",
      "iPhone",
      "iPod",
    ].includes(navigator.platform) ||
    (navigator.userAgent.includes("Mac") && "ontouchend" in document)
  );
};

const isAndroid = () => /Android/i.test(navigator.userAgent);

// 检测是否已经安装
const isStandalone = () => {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    (window.navigator as any).standalone ||
    document.referrer.includes("android-app://")
  );
};

// 注册 Service Worker
const updateSW = registerSW({
  immediate: true,
  onNeedRefresh() {
    updateSW(true);
  },
  onOfflineReady() {
    console.log("App está pronto para uso offline");
  },
});

// 监听 beforeinstallprompt 事件
const handleBeforeInstallPrompt = (e: Event) => {
  e.preventDefault();
  deferredPrompt = e;
  canInstall.value = true;
  if (!getIsApk() && !isStandalone() && !autoPromptClosed.value) {
    showInstallPrompt.value = true;
  }
};

if (!getIsApk() && !isStandalone() && !isIOS()) {
  window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
  window.addEventListener("appinstalled", () => {
    showInstallPrompt.value = false;
    canInstall.value = false;
    autoPromptClosed.value = true;
    deferredPrompt = null;
  });
}
const isChromeOnAndroid = () => {
  const ua = navigator.userAgent;
  // 3. 排除 Edge、Opera、QQ浏览器等
  const isOtherChromium =
    /Edg|OPR|Firefox|QQBrowser|SamsungBrowser|UCBrowser|MicroMessenger/i.test(
      ua
    );
  return /Android/i.test(ua) && /Chrome/i.test(ua) && !isOtherChromium;
};

const isInAppBrowser = () => {
  const ua = navigator.userAgent || "";
  return /MicroMessenger|QQBrowser|UCBrowser|FBAN|FBAV|Instagram|Line|TikTok|Twitter/i.test(ua);
};
//跳转google浏览器
const openChrome = () => {
  if (!isAndroid()) return;
  if (location.hostname === "localhost" || location.hostname === "127.0.0.1") {
    showWarning("No ambiente local, abra no Chrome manualmente");
    return;
  }
  try {
    var e = new URL(window.location.href);
    e.searchParams.set("is_open_chrome", "1");
    const scheme = e.protocol.replace(":", "");
    const chromeIntent = `intent://${e.href.replace(
      /(https|http):\/\//,
      ""
    )}#Intent;package=com.android.chrome;scheme=${scheme};end`;
    window.location.href = chromeIntent;
  } catch (error) {
    console.log(error);
  }
};
//判断是否google浏览器的方法
// 处理安装按钮点击
let waitForBIPTimeout: any = null;
const handleInstall = async () => {
  if (getIsApk()) return;
  if (isIOS()) {
    showWarning('No iPhone: use Safari → Compartilhar → "Adicionar à Tela de Início"');
    return;
  }
  if (isInAppBrowser()) {
    showWarning("Abra no Chrome para instalar");
    openChrome();
    return;
  }

  if (!deferredPrompt) {
    if (isAndroid()) {
      if (!isChromeOnAndroid()) {
        showWarning("Abra no Chrome para instalar");
        openChrome();
        return;
      }
      showWarning('No Chrome: menu (⋮) → "Instalar app"');
      return;
    }
    showWarning('No Chrome (PC): clique no ícone de instalação na barra de endereço');
    return;
  }

  // 显示安装提示
  deferredPrompt.prompt();

  // 等待用户响应
  const { outcome } = await deferredPrompt.userChoice;

  // 根据用户选择处理结果
  if (outcome === "accepted") {
    console.log("Usuário aceitou a instalação");
  } else {
    console.log("Usuário recusou a instalação");
  }

  // 清除 deferredPrompt
  deferredPrompt = null;
  canInstall.value = false;
  autoPromptClosed.value = true;
};

const closeAutoPrompt = () => {
  showInstallPrompt.value = false;
  autoPromptClosed.value = true;
};

const confirmAutoPrompt = async () => {
  showInstallPrompt.value = false;
  await handleInstall();
};

onMounted(() => {
  if (getIsApk() || isStandalone()) return;
  if (!autoPromptClosed.value) {
    showInstallPrompt.value = true;
  }
  (window as any).__pwaInstall = { handleInstall };
});

onUnmounted(() => {
  window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
  if ((window as any).__pwaInstall?.handleInstall === handleInstall) {
    delete (window as any).__pwaInstall;
  }
});

defineExpose({ handleInstall });
</script>

<style scoped lang="scss">
.pwa-install-btn {
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  cursor: pointer;
  transition: all 0.3s ease;

  &:active {
    transform: scale(0.95);
  }

  img {
    width: 48px;
    height: 48px;
  }
}

.pwa-overlay {
  :deep(.v-overlay__content) {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    width: auto;
    max-width: 100%;
    margin: 0;
    transform: none;
  }
}

@media (min-width: 601px) {
  .pwa-overlay {
    :deep(.v-overlay__content) {
      left: 50%;
      right: auto;
      width: 520px;
      max-width: calc(100% - 24px);
      bottom: 12px;
      transform: translateX(-50%);
    }
  }
}

.pwa-bar {
  width: 100%;
  background: #0b1220;
  border-radius: 16px;
  padding: 12px 14px;
  align-items: center;
  gap: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.45);
  .pwa-content {
    display: flex;
    align-items: center;
    gap: 12px;
  }
}

.pwa-icon {
  width: 46px;
  height: 46px;
  border-radius: 12px;
  flex-shrink: 0;
}

.pwa-message {
  color: #ffffff;
  font-size: 14px;
  line-height: 1.3;
  font-weight: 700;
  text-align: left;
  flex: 1;
  min-width: 0;
}

.pwa-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
  margin-top: 12px;
  justify-content: flex-end;
}

.pwa-action {
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
}

.pwa-action.cancel {
  color: rgba(255, 255, 255, 0.9);
}

.pwa-action.install {
  background: #3b82f6;
  color: #fff;
  padding: 8px 16px;
  border-radius: 12px;
}
</style>
